import { objectModule } from "@/modules/object";
import { logger } from "@/modules/logger";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { action, computed, makeObservable, observable } from "mobx";
import { toastModule } from "@/modules/toast";
import type { Uuid } from "@/domains/global/identifiers";

type SystemAudioRecordingFailedModalState = {
  audioRecordingId: Uuid;
  noteId: Uuid | null;
  onConfirm: () => Promise<void> | void;
  onRetry: () => Promise<void> | void;
  onCancel?: () => void;
};

export class SystemAudioRecordingFailedModalStore extends AppSubStore {
  private state: SystemAudioRecordingFailedModalState | null = null;
  private isConfirmingValue = false;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    makeObservable<this, "state" | "isConfirmingValue">(this, {
      state: observable.ref,
      isConfirmingValue: observable,
      isOpen: computed,
      isConfirming: computed,
      open: action,
      close: action,
      handleConfirm: action,
      handleRetry: action,
      handleClose: action,
      title: computed,
      message: computed,
      confirmLabel: computed,
      retryLabel: computed,
    });
  }

  get title(): string {
    return "Voice mode can't connect to the internet";
  }

  get message(): string {
    return "When offline, voice mode only receives audio from your microphone. Audio from other sources, such as meeting participants or videos, is not available.";
  }

  get confirmLabel(): string {
    return "Record only my microphone";
  }

  get retryLabel(): string {
    return "Retry";
  }

  get isOpen(): boolean {
    return Boolean(this.state);
  }

  get isConfirming(): boolean {
    return this.isConfirmingValue;
  }

  open(state: SystemAudioRecordingFailedModalState): void {
    this.state = state;
    this.isConfirmingValue = false;
  }

  close(): void {
    if (!this.isOpen) return;

    this.state = null;
    this.isConfirmingValue = false;
  }

  async handleConfirm(): Promise<void> {
    const currentState = this.state;
    if (!currentState) return;
    if (this.isConfirmingValue) return;

    this.isConfirmingValue = true;

    try {
      this.close();
      await currentState.onConfirm?.();
    } catch (unknownErr) {
      logger.warn({
        message: "[SystemAudioRecordingFailedModalStore] handleConfirm failed",
        info: { error: objectModule.safeErrorAsJson(unknownErr as Error) },
      });

      toastModule.triggerToast({
        content: "Something went wrong. Please try again.",
        toastId: "system-audio-recording-failed-modal-error",
      });

      this.isConfirmingValue = false;
    }
  }

  async handleRetry(): Promise<void> {
    const currentState = this.state;
    if (!currentState) return;
    if (this.isConfirmingValue) return;

    this.isConfirmingValue = true;

    try {
      this.close();
      await currentState.onRetry?.();
    } catch (unknownErr) {
      logger.warn({
        message: "[SystemAudioRecordingFailedModalStore] handleRetry failed",
        info: { error: objectModule.safeErrorAsJson(unknownErr as Error) },
      });
    }
  }

  handleClose(): void {
    if (this.isConfirmingValue) return;
    const currentState = this.state;
    this.close();
    currentState?.onCancel?.();
  }
}
